import type { Page } from 'playwright';

export type PlaywrightTabPage = Pick<Page, 'url' | 'title'>;

export interface TabSelectorObject {
  index?: number;
  handle?: number;
  match?: string;
}

export type TabSelector = number | TabSelectorObject;

export interface ListedTab {
  index: number;
  handle: number;
  url: string;
  title: string;
  active: boolean;
}

export interface SelectedTab {
  index: number;
  handle: number;
}

export interface ClosedTabResult {
  closed: true;
  remaining: number;
  active: SelectedTab | null;
}

export interface NoTabMatchResult {
  error: string;
}

export class PlaywrightTabRegistry<TPage extends PlaywrightTabPage = Page> {
  private nextPageId = 0;
  private pageToId = new WeakMap<TPage, number>();
  private idToPage = new Map<number, TPage>();

  assign(page: TPage): number {
    const existing = this.pageToId.get(page);
    if (existing !== undefined) {
      this.idToPage.set(existing, page);
      return existing;
    }
    const id = this.nextPageId++;
    this.pageToId.set(page, id);
    this.idToPage.set(id, page);
    return id;
  }

  resolveHandle(handle: number, pages: readonly TPage[]): TPage | null {
    const page = this.idToPage.get(handle);
    return page && pages.includes(page) ? page : null;
  }

  prune(page: TPage): void {
    const id = this.pageToId.get(page);
    if (id !== undefined) {
      this.idToPage.delete(id);
    }
  }

  reset(): void {
    this.nextPageId = 0;
    this.pageToId = new WeakMap<TPage, number>();
    this.idToPage = new Map<number, TPage>();
  }
}

export function normalizeTabSelector(selector?: TabSelector): TabSelectorObject {
  return typeof selector === 'number' ? { index: selector } : (selector ?? {});
}

export function hasExplicitTabSelector(selector: TabSelectorObject): boolean {
  return (
    selector.handle !== undefined || selector.match !== undefined || selector.index !== undefined
  );
}

export async function resolveTabTarget<TPage extends PlaywrightTabPage>(
  pages: readonly TPage[],
  selector: TabSelectorObject,
  registry: PlaywrightTabRegistry<TPage>
): Promise<TPage | null> {
  if (selector.handle !== undefined) {
    return registry.resolveHandle(selector.handle, pages);
  }
  if (selector.match) {
    const needle = selector.match.toLowerCase();
    for (const page of pages) {
      const url = safePageUrl(page).toLowerCase();
      const title = (await safePageTitle(page)).toLowerCase();
      if (url.includes(needle) || title.includes(needle)) return page;
    }
    return null;
  }
  if (selector.index !== undefined) return pages[selector.index] ?? null;
  return null;
}

export async function listTabs<TPage extends PlaywrightTabPage>(
  pages: readonly TPage[],
  activePage: TPage | null,
  registry: PlaywrightTabRegistry<TPage>
): Promise<ListedTab[]> {
  return Promise.all(
    pages.map(async (page, index) => ({
      index,
      handle: registry.assign(page),
      url: safePageUrl(page),
      title: await safePageTitle(page),
      active: page === activePage,
    }))
  );
}

export function listOpenPageHandles<TPage extends PlaywrightTabPage>(
  pages: readonly TPage[],
  registry: PlaywrightTabRegistry<TPage>
): Array<{ handle: number; url: string }> {
  return pages.map((page) => ({
    handle: registry.assign(page),
    url: safePageUrl(page),
  }));
}

export function buildNoTabMatchResult(selector: TabSelectorObject): NoTabMatchResult {
  return {
    error: `No tab matched ${JSON.stringify(selector)}. Call browser_tabs(action:'list') to see open tabs.`,
  };
}

export function chooseActivePageAfterClose<TPage extends PlaywrightTabPage>(input: {
  closingActivePage: boolean;
  currentActivePage: TPage | null;
  remainingPages: readonly TPage[];
}): TPage | null {
  if (input.closingActivePage || !input.currentActivePage) {
    return input.remainingPages[0] ?? null;
  }
  return input.currentActivePage;
}

export function buildSelectedTabResult<TPage extends PlaywrightTabPage>(
  pages: readonly TPage[],
  page: TPage,
  registry: PlaywrightTabRegistry<TPage>
): SelectedTab {
  return {
    index: pages.indexOf(page),
    handle: registry.assign(page),
  };
}

export function buildClosedTabResult<TPage extends PlaywrightTabPage>(
  remainingPages: readonly TPage[],
  activePage: TPage | null,
  registry: PlaywrightTabRegistry<TPage>
): ClosedTabResult {
  return {
    closed: true,
    remaining: remainingPages.length,
    active: activePage ? buildSelectedTabResult(remainingPages, activePage, registry) : null,
  };
}

async function safePageTitle(page: PlaywrightTabPage): Promise<string> {
  return await page.title().catch(() => '');
}

function safePageUrl(page: PlaywrightTabPage): string {
  try {
    return page.url();
  } catch {
    return '';
  }
}
